import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, Workflow, Users, Send, BarChart3, Inbox, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/marketing/email-automation/")({
  component: EmailAutomationIndex,
  head: () => ({
    meta: [
      { title: "Email & Automation — CoderLity" },
      { name: "description", content: "Lifecycle email campaigns and automation flows that nurture leads and convert customers." },
      { property: "og:title", content: "Email & Automation — CoderLity" },
      { property: "og:description", content: "Lifecycle email, SMS and automation flows that turn lists into revenue." },
      { property: "og:url", content: "/marketing/email-automation" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/email-automation" }],
  }),
});

const email = [
  { slug: "email-campaigns", title: "Email Campaigns", desc: "Explore email campaigns" },
  { slug: "newsletter-strategy", title: "Newsletter Strategy", desc: "Explore newsletter strategy" },
  { slug: "transactional-email", title: "Transactional Email", desc: "Explore transactional email" },
];

const automation = [
  { slug: "marketing-automation", title: "Marketing Automation", desc: "Explore marketing automation" },
  { slug: "crm-integration", title: "CRM Integration", desc: "Explore crm integration" },
  { slug: "lead-scoring", title: "Lead Scoring", desc: "Explore lead scoring" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/marketing/email-automation/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmailAutomationIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Email" items={email} />
        <Group title="Automation" items={automation} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Marketing",
  title: "Email & Automation",
  sub: "Lifecycle campaigns that nurture and convert.",
  intro:
    "Email is still the highest-ROI channel in marketing — when it's done well. We build lifecycle programs that meet customers at every moment: welcome flows, abandoned cart, post-purchase, win-back, VIP and product launches. Every email is segmented, designed, deliverability-tuned and tied to revenue.",
  features: [
    { icon: Workflow, title: "Lifecycle flows", description: "Welcome, abandoned cart, post-purchase, win-back and more." },
    { icon: Users, title: "Segmentation", description: "Behavior, RFM and predictive segments that send relevant emails." },
    { icon: Mail, title: "Campaign emails", description: "Weekly newsletters, launches and seasonal promotions." },
    { icon: Send, title: "SMS + push", description: "Multi-channel lifecycle that goes beyond inbox." },
    { icon: Inbox, title: "Deliverability", description: "DMARC, SPF, DKIM, IP warming and sender reputation monitoring." },
    { icon: BarChart3, title: "Revenue reporting", description: "Per-flow, per-segment revenue attribution and LTV impact." },
  ],
  process: [
    { step: "01", title: "Audit", description: "List health, deliverability and existing flow performance." },
    { step: "02", title: "Architecture", description: "Map every lifecycle moment to a flow or campaign." },
    { step: "03", title: "Build", description: "Design, copy, segments, A/B tests and tracking." },
    { step: "04", title: "Optimize", description: "Continuous testing of subject lines, copy and offers." },
  ],
  deliverables: [
    "List hygiene and deliverability audit",
    "8–15 lifecycle flows built and live",
    "4 campaign emails per month",
    "Custom segments and predictive scoring",
    "A/B test program and reporting",
    "Monthly revenue and engagement reports",
  ],
  techStack: ["Klaviyo", "HubSpot", "Customer.io", "Postmark", "Resend", "Mailgun", "Segment", "Attentive"],
  pricing: [
    { name: "Flows only", price: "$2.5k", period: "month", features: ["5 core flows built", "Deliverability setup", "Monthly reporting", "Email design system"] },
    { name: "Full lifecycle", price: "$5k", period: "month", highlighted: true, features: ["12+ flows", "4 campaigns / month", "Segmentation strategy", "A/B testing"] },
    { name: "Enterprise", price: "$10k", period: "month", features: ["Email + SMS + push", "Predictive segments", "Dedicated strategist", "Custom integrations"] },
  ],
  faqs: [
    { q: "Which ESP should we use?", a: "Klaviyo for e-commerce, HubSpot for B2B, Customer.io for SaaS. We help you choose based on stack and use case." },
    { q: "Do you handle deliverability issues?", a: "Yes — DMARC, SPF, DKIM, sender reputation monitoring and IP warming when needed." },
    { q: "Will you write copy?", a: "Yes. Our email copywriters work in your brand voice with clear CTAs tied to conversion goals." },
    { q: "Can you integrate with our backend?", a: "Yes. We connect your ESP to Shopify, custom databases, CRMs and event streams via Segment or direct APIs." },
  ],
};
